/**
 * Path Validation and Normalization (§20 §4.3)
 *
 * SecureBlob uses a virtual POSIX-like filesystem with `/` as the sole separator.
 * All paths are absolute (start with `/`). Names (individual segments) must NOT
 * contain path separators, traversal markers, or null bytes.
 *
 * This module is the single source of truth for path rules, shared by
 * session, dirTreeCache, commitPipeline, and realWorkspace.
 */

import { SdkError } from './errors'

// ── Name Validation ──────────────────────────────────────────────

/** Characters forbidden in a single name segment. */
const FORBIDDEN_IN_NAME = /[\\/\0]/

/** Reserved names that would cause traversal or confusion. */
const RESERVED_NAMES = new Set(['.', '..'])

/**
 * Validate a single directory/file name (NOT a full path).
 * Throws `INVALID_PATH` if the name is invalid.
 *
 * Rules:
 *   - Must be non-empty
 *   - Must not contain `/` or `\` (path separators) or `\0` (null byte)
 *   - Must not be `.` or `..` (traversal)
 *   - No length limit enforced here (filesystem-level limits apply at higher layers)
 */
export function validateName(name: string): void {
    if (!name || name.length === 0) {
        throw new SdkError('INVALID_PATH', 'Name must not be empty')
    }
    if (RESERVED_NAMES.has(name)) {
        throw new SdkError('INVALID_PATH', `Reserved name: "${name}"`)
    }
    if (FORBIDDEN_IN_NAME.test(name)) {
        throw new SdkError('INVALID_PATH', `Name contains forbidden character: "${name}"`)
    }
}

// ── Path Normalization ───────────────────────────────────────────

/**
 * Normalize an input path to canonical form:
 *   1. Backslashes → forward slashes (cross-platform convenience)
 *   2. Collapse multiple consecutive slashes
 *   3. Remove trailing slash (except root `/`)
 *   4. Ensure starts with `/`
 *
 * Does NOT resolve `.` or `..` — those are rejected by validatePath.
 *
 * Examples:
 *   "docs\\readme.md"  → "/docs/readme.md"
 *   "//a///b//"        → "/a/b"
 *   "/"                → "/"
 *   "a/b"              → "/a/b"
 */
export function normalizePath(path: string): string {
    // Step 1: Backslash → forward slash
    let p = path.replace(/\\/g, '/')

    // Step 2: Ensure leading slash
    if (!p.startsWith('/')) p = '/' + p

    // Step 3: Collapse multiple slashes
    p = p.replace(/\/+/g, '/')

    // Step 4: Remove trailing slash (except root)
    if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1)

    return p
}

// ── Path Validation ──────────────────────────────────────────────

/**
 * Validate a full absolute path.
 * Normalizes the path first, then validates each segment.
 * Returns the normalized path.
 *
 * Throws `INVALID_PATH` for:
 *   - Segments containing `/`, `\`, or `\0`
 *   - Segments that are `.` or `..` (traversal)
 *   - Empty segments (after normalization, this shouldn't happen)
 */
export function validatePath(path: string): string {
    if (!path || path.length === 0) {
        throw new SdkError('INVALID_PATH', 'Path must not be empty')
    }

    // Validate absolute-path requirement before normalizePath() auto-prefixes '/'.
    // Spec §80 §8.3: paths MUST be absolute; normalizePath convenience must not
    // silently accept relative paths in write operations.
    const slashNormalized = path.replace(/\\/g, '/')
    if (!slashNormalized.startsWith('/')) {
        throw new SdkError('INVALID_PATH', `Path must be absolute: "${path}"`)
    }

    const normalized = normalizePath(slashNormalized)

    // Root path is always valid
    if (normalized === '/') return normalized

    const segments = normalized.slice(1).split('/')
    for (const seg of segments) {
        validateName(seg)
    }

    return normalized
}

// ── Segment Splitting ────────────────────────────────────────────

/**
 * Split a validated path into segments.
 * The path MUST already be normalized (via validatePath or normalizePath).
 *
 * Examples:
 *   "/docs/api/spec.md" → ["docs", "api", "spec.md"]
 *   "/"                 → []
 */
export function pathSegments(normalizedPath: string): string[] {
    if (normalizedPath === '/') return []
    return normalizedPath.slice(1).split('/')
}

/**
 * Split a validated path into parent directory and final name.
 *
 * Examples:
 *   "/docs/api/spec.md" → { parent: "/docs/api", name: "spec.md" }
 *   "/readme.md"        → { parent: "/", name: "readme.md" }
 *   "/"                 → throws (root has no name)
 */
export function splitParentName(normalizedPath: string): { parent: string; name: string } {
    if (normalizedPath === '/') {
        throw new SdkError('INVALID_PATH', 'Root path has no parent/name split')
    }
    const segments = pathSegments(normalizedPath)
    const name = segments[segments.length - 1]
    const parent = segments.length === 1 ? '/' : '/' + segments.slice(0, -1).join('/')
    return { parent, name }
}
